import { useState, useEffect, useMemo } from 'react'
import { publicAPI } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'
import SearchBar from '../components/SearchBar'
import PostnummerFilter from '../components/PostnummerFilter'
import ExportButtons from '../components/ExportButtons'
import DateFilterBar, { OutputMode, getDateRangeForMode, formatNorwegianDate } from '../components/DateFilterBar'
import { useYearDates } from '../contexts/YearContext'
import { useVehicleFilter } from '../contexts/VehicleFilterContext'
import PeriodInfoBanner from '../components/PeriodInfoBanner'
import { useMaxDataDate } from '../hooks/useMaxDataDate'
import { Car, TrendingUp, Package, Zap, Building2, ChevronRight, ArrowLeft, ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react'
import { Bar } from 'react-chartjs-2'
import { ColumnDefinition } from '../utils/exportToCsv'
import { usePersistedState } from '../hooks/usePersistedState'
import { useMinePostnummer } from '../hooks/useMinePostnummer'
import { useTranslation } from 'react-i18next'
import { localizeApiDetail } from '../i18n/apiError'

// Helper function to safely parse numeric values that might be strings
const toNumber = (value: number | string | null | undefined): number => {
  if (value === null || value === undefined) return 0
  return typeof value === 'string' ? parseFloat(value) : value
}

interface MasterModelStats {
  ranking: number | null // null for summary row, 1-N for models
  master_model_id: number | null // null for summary row
  brand_key: string | null // null for summary row
  brand: string // "TOTAL" for summary row
  master_model: string // "All Models" for summary row
  segment_id: number | null
  body_type: string | null
  total_count: number
  market_share_percentage: number // 100.0 for summary row
  is_electric: 0 | 1
  ev_count: number
  ev_percentage: number
  leasing_count: number
  leasing_percentage: number
  private_leasing_count: number
  private_leasing_percentage: number
  company_leasing_count: number
  company_leasing_percentage: number
  undefined_leasing_count: number
  undefined_leasing_percentage: number
  company_sales_count: number
  company_sales_percentage: number
  avg_owner_age: number | null
}

// Model Variant interface (for future use when backend endpoint is ready)
interface ModelVariantStats {
  model_variant: string
  brand: string
  total_count: number
  market_share_percentage: number
  ev_count: number
  ev_percentage: number
  leasing_count: number
  leasing_percentage: number
  private_leasing_count: number
  private_leasing_percentage: number
  company_leasing_count: number
  company_leasing_percentage: number
  undefined_leasing_count: number
  undefined_leasing_percentage: number
  company_sales_count: number
  company_sales_percentage: number
  avg_owner_age: number | null
}

export default function MasterModelAnalytics() {
  const { t } = useTranslation('analytics')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [masterModels, setMasterModels] = useState<MasterModelStats[]>([])
  const [filteredMasterModels, setFilteredMasterModels] = useState<MasterModelStats[]>([])
  const [masterModelSummary, setMasterModelSummary] = useState<MasterModelStats | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedBrand, setSelectedBrand] = useState<string>('')
  const [selectedBrandKey, setSelectedBrandKey] = useState<string>('')
  const [allBrands, setAllBrands] = useState<Array<{brand: string, brand_key: string}>>([])
  
  // Date range state from year context
  const { startDate, setStartDate, endDate, setEndDate, selectedYear, maxSvvDate } = useYearDates('master-model-analytics')
  const [minCount, setMinCount] = useState<number>(0)

  // DateFilterBar state
  const [outputMode, setOutputMode] = usePersistedState<OutputMode>('bp:filter:outputMode', 'year_to_date')
  const [customStartDate, setCustomStartDate] = usePersistedState('bp:filter:customStartDate', '')
  const [customEndDate, setCustomEndDate] = usePersistedState('bp:filter:customEndDate', '')
  const maxDataDate = useMaxDataDate()
  
  // Vehicle filter
  const { vehicleFilter, getVarebilParam } = useVehicleFilter()
  
  // Postal code filter state
  const [postnummerFilterEnabled, setPostnummerFilterEnabled] = usePersistedState('master-model-analytics:postnummerFilterEnabled', false)
  const minePostnummer = useMinePostnummer(postnummerFilterEnabled)
  
  // Date range validation
  const [dateRangeAlert, setDateRangeAlert] = useState<string | null>(null)
  
  // Model Variants view state (when master model is selected)
  const [selectedMasterModel, setSelectedMasterModel] = useState<MasterModelStats | null>(null)
  const [modelVariants, setModelVariants] = useState<ModelVariantStats[]>([])
  const [variantsLoading, setVariantsLoading] = useState(false)
  const [variantsError, setVariantsError] = useState<string | null>(null)

  // Sort state
  const [masterModelSortConfig, setMasterModelSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)
  const [variantSortConfig, setVariantSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)

  useEffect(() => {
    fetchMasterModels()
  }, [selectedYear, vehicleFilter])

  // Validate date range (max 366 days)
  const validateDateRange = (start: Date, end: Date): boolean => {
    const diffTime = Math.abs(end.getTime() - start.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays > 366) {
      setDateRangeAlert(t('common.dateRangeTooLarge', { days: diffDays }))
      return false
    }
    
    setDateRangeAlert(null)
    return true
  }

  useEffect(() => {
    // Only filter by search term
    // Brand filtering is handled by API call
    const filtered = masterModels.filter(mm =>
      mm.master_model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mm.brand?.toLowerCase().includes(searchTerm.toLowerCase())
    )

    setFilteredMasterModels(filtered)
  }, [masterModels, searchTerm])

  const fetchMasterModels = async () => {
    try {
      setLoading(true)
      setError(null)

      const start_date = startDate.toISOString().split('T')[0]
      const end_date = endDate.toISOString().split('T')[0]

      const varebilParam = getVarebilParam()
      const params: any = { 
        start_date, 
        end_date, 
        limit: 200 
      }
      
      if (varebilParam !== undefined) {
        params.varebil = varebilParam
      }
      
      if (minCount > 0) {
        params.min_count = minCount
      }

      // Add brand_key filter if a brand is selected
      if (selectedBrandKey) {
        params.brand_key = selectedBrandKey
      }

      // Add postal code filter if enabled
      if (minePostnummer) {
        params.mine_postnummer = minePostnummer
      }

      const response = await publicAPI.getMasterModels(params)
      const data = response.data.data || []
      
      // First item is the summary row (ranking === null), rest are model rows
      const [apiSummary, ...modelRows] = data
      setMasterModels(modelRows.filter((m: MasterModelStats) => m.ranking !== null))

      // Use the summary row from master-models endpoint
      if (apiSummary && apiSummary.ranking === null) {
        setMasterModelSummary({
          ...apiSummary,
          brand: 'TOTAL',
          master_model: 'All Models',
          undefined_leasing_count: apiSummary.undefined_leasing_count || 0,
          undefined_leasing_percentage: apiSummary.undefined_leasing_percentage || 0
        })
      }

      // Extract unique brands for dropdown (only on initial load without brand filter)
      if (!selectedBrandKey && modelRows.length > 0) {
        const uniqueBrandsMap = new Map<string, {brand: string, brand_key: string}>()
        modelRows.forEach((mm: MasterModelStats) => {
          if (mm.ranking !== null && mm.brand_key && !uniqueBrandsMap.has(mm.brand)) {
            uniqueBrandsMap.set(mm.brand, { brand: mm.brand, brand_key: mm.brand_key })
          }
        })
        setAllBrands(Array.from(uniqueBrandsMap.values()).sort((a, b) => a.brand.localeCompare(b.brand)))
      }
    } catch (err: any) {
      setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('common.loadFailed'))
    } finally {
      setLoading(false)
    }
  }

  const handleBrandChange = async (brand: string) => {
    setSelectedBrand(brand)
    
    // Find the brand_key for the selected brand
    const brandInfo = allBrands.find(b => b.brand === brand)
    const newBrandKey = brandInfo?.brand_key || ''
    setSelectedBrandKey(newBrandKey)
    
    // Trigger new API call with brand filter
    // If brand is cleared, fetch all brands again
    try {
      setLoading(true)
      setError(null)

      const start_date = startDate.toISOString().split('T')[0]
      const end_date = endDate.toISOString().split('T')[0]

      const varebilParam = getVarebilParam()
      const params: any = { 
        start_date, 
        end_date, 
        limit: 200 
      }
      
      if (varebilParam !== undefined) {
        params.varebil = varebilParam
      }
      
      if (minCount > 0) {
        params.min_count = minCount
      }

      // Add brand_key filter if a brand is selected
      if (newBrandKey) {
        params.brand_key = newBrandKey
      }

      // Add postal code filter if enabled
      if (minePostnummer) {
        params.mine_postnummer = minePostnummer
      }

      const response = await publicAPI.getMasterModels(params)
      const data = response.data.data || []
      
      // First item is the summary row (ranking === null), rest are model rows
      const [apiSummary, ...modelRows] = data
      setMasterModels(modelRows.filter((m: MasterModelStats) => m.ranking !== null))

      // Use the summary row from master-models endpoint
      if (apiSummary && apiSummary.ranking === null) {
        setMasterModelSummary({
          ...apiSummary,
          brand: '-',
          master_model: 'TOTAL',
          undefined_leasing_count: apiSummary.undefined_leasing_count || 0,
          undefined_leasing_percentage: apiSummary.undefined_leasing_percentage || 0
        })
      }
    } catch (err: any) {
      setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('common.loadFailed'))
    } finally {
      setLoading(false)
    }
  }

  const handleApplyFilters = () => {
    if (outputMode === 'custom') {
      if (customStartDate && customEndDate) {
        const s = new Date(customStartDate)
        const e = new Date(customEndDate)
        if (!isNaN(s.getTime()) && !isNaN(e.getTime()) && validateDateRange(s, e)) {
          setStartDate(s)
          setEndDate(e)
          setTimeout(() => fetchMasterModels(), 0)
        }
      }
    } else {
      const { startDate: s, endDate: e } = getDateRangeForMode(outputMode, maxSvvDate ?? undefined)
      setStartDate(new Date(s))
      setEndDate(new Date(e))
      setDateRangeAlert(null)
      setTimeout(() => fetchMasterModels(), 0)
    }
  }

  const handleOutputModeChange = (mode: OutputMode) => {
    setOutputMode(mode)
    if (mode !== 'custom') {
      const { startDate: s, endDate: e } = getDateRangeForMode(mode, maxSvvDate ?? undefined)
      setStartDate(new Date(s))
      setEndDate(new Date(e))
      setDateRangeAlert(null)
      setTimeout(() => fetchMasterModels(), 0)
    }
  }


  // Fetch model variants for a selected master model
  const fetchMasterModelVariants = async (masterModel: MasterModelStats) => {
    try {
      setVariantsLoading(true)
      setVariantsError(null)

      const start_date = startDate.toISOString().split('T')[0]
      const end_date = endDate.toISOString().split('T')[0]

      const varebilParam = getVarebilParam()
      const params: any = { start_date, end_date, limit: 100 }
      
      if (varebilParam !== undefined) {
        params.varebil = varebilParam
      }
      
      // Add postal code filter if enabled
      if (minePostnummer) {
        params.mine_postnummer = minePostnummer
      }

      const response = await publicAPI.getMasterModelVariants(masterModel.master_model_id!, params)
      setModelVariants(response.data.data || [])
    } catch (err: any) {
      console.error('Model variants API error:', err)
      setVariantsError(localizeApiDetail(err.response?.data?.detail) || err.message || t('common.loadVariantsFailed'))
    } finally {
      setVariantsLoading(false)
    }
  }

  // User clicks a master model to see variants
  const handleMasterModelClick = (masterModel: MasterModelStats) => {
    setSelectedMasterModel(masterModel)
    fetchMasterModelVariants(masterModel)
  }

  // Back from variants view to master models list
  const handleBackToList = () => {
    setSelectedMasterModel(null)
    setModelVariants([])
    setVariantsError(null)
    setVariantSortConfig(null)
  }

  // Calculate total company vehicles
  const getTotalCompanyVehicles = (model: MasterModelStats) => {
    return model.company_leasing_count + model.company_sales_count
  }

  // Sort handler for master models
  const handleMasterModelSort = (key: string) => {
    if (masterModelSortConfig?.key === key) {
      if (masterModelSortConfig.direction === 'asc') {
        setMasterModelSortConfig({ key, direction: 'desc' })
      } else {
        setMasterModelSortConfig(null)
      }
    } else {
      setMasterModelSortConfig({ key, direction: 'asc' })
    }
  }

  // Get sort icon for master models
  const getMasterModelSortIcon = (columnKey: string) => {
    if (masterModelSortConfig?.key === columnKey) {
      return masterModelSortConfig.direction === 'asc' ? 
        <ArrowUp className="w-4 h-4 inline ml-1" /> : 
        <ArrowDown className="w-4 h-4 inline ml-1" />
    }
    return <ChevronsUpDown className="w-4 h-4 inline ml-1 opacity-40" />
  }

  // Sort handler for variants
  const handleVariantSort = (key: string) => {
    if (variantSortConfig?.key === key) {
      if (variantSortConfig.direction === 'asc') {
        setVariantSortConfig({ key, direction: 'desc' })
      } else {
        setVariantSortConfig(null)
      }
    } else {
      setVariantSortConfig({ key, direction: 'asc' })
    }
  }

  // Get sort icon for variants
  const getVariantSortIcon = (columnKey: string) => {
    if (variantSortConfig?.key === columnKey) {
      return variantSortConfig.direction === 'asc' ? 
        <ArrowUp className="w-4 h-4 inline ml-1" /> : 
        <ArrowDown className="w-4 h-4 inline ml-1" />
    }
    return <ChevronsUpDown className="w-4 h-4 inline ml-1 opacity-40" />
  }

  // Sorted master models data
  const sortedMasterModels = useMemo(() => {
    if (!masterModelSortConfig) return filteredMasterModels

    const sorted = [...filteredMasterModels].sort((a, b) => {
      let aVal: any
      let bVal: any

      switch (masterModelSortConfig.key) {
        case 'ranking':
          aVal = a.ranking !== null ? a.ranking : Infinity
          bVal = b.ranking !== null ? b.ranking : Infinity
          break
        case 'brand':
          aVal = a.brand.toLowerCase()
          bVal = b.brand.toLowerCase()
          break
        case 'master_model':
          aVal = a.master_model.toLowerCase()
          bVal = b.master_model.toLowerCase()
          break
        case 'body_type':
          aVal = (a.body_type || '').toLowerCase()
          bVal = (b.body_type || '').toLowerCase()
          break
        case 'total_count':
          aVal = toNumber(a.total_count)
          bVal = toNumber(b.total_count)
          break
        case 'market_share_percentage':
          aVal = toNumber(a.market_share_percentage)
          bVal = toNumber(b.market_share_percentage)
          break
        case 'leasing_percentage':
          aVal = toNumber(a.leasing_percentage)
          bVal = toNumber(b.leasing_percentage)
          break
        case 'company_total':
          aVal = getTotalCompanyVehicles(a)
          bVal = getTotalCompanyVehicles(b)
          break
        case 'avg_owner_age':
          aVal = a.avg_owner_age !== null ? toNumber(a.avg_owner_age) : -Infinity
          bVal = b.avg_owner_age !== null ? toNumber(b.avg_owner_age) : -Infinity
          break
        default:
          return 0
      }

      if (typeof aVal === 'string') {
        return masterModelSortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }
      return masterModelSortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal
    })

    return sorted
  }, [filteredMasterModels, masterModelSortConfig])

  // Sorted variants data
  const sortedVariants = useMemo(() => {
    if (!variantSortConfig) return modelVariants

    const sorted = [...modelVariants].sort((a, b) => {
      let aVal: any
      let bVal: any

      switch (variantSortConfig.key) {
        case 'model_variant':
          aVal = a.model_variant.toLowerCase()
          bVal = b.model_variant.toLowerCase()
          break
        case 'total_count':
          aVal = toNumber(a.total_count)
          bVal = toNumber(b.total_count)
          break
        case 'market_share_percentage':
          aVal = toNumber(a.market_share_percentage)
          bVal = toNumber(b.market_share_percentage)
          break
        case 'ev_percentage':
          aVal = toNumber(a.ev_percentage)
          bVal = toNumber(b.ev_percentage)
          break
        case 'leasing_percentage':
          aVal = toNumber(a.leasing_percentage)
          bVal = toNumber(b.leasing_percentage)
          break
        case 'company_percentage':
          aVal = toNumber(a.company_sales_percentage) + toNumber(a.company_leasing_percentage)
          bVal = toNumber(b.company_sales_percentage) + toNumber(b.company_leasing_percentage)
          break
        default:
          return 0
      }

      if (typeof aVal === 'string') {
        return variantSortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }
      return variantSortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal
    })

    return sorted
  }, [modelVariants, variantSortConfig])

  // Column definitions for export
  const masterModelColumns: ColumnDefinition[] = [
    { key: 'ranking', label: '#' },
    { key: 'master_model', label: t('common.masterModel') },
    { key: 'brand', label: t('common.brand') },
    { key: 'body_type', label: 'Type' },
    { 
      key: 'total_count', 
      label: t('common.registrations'),
      format: (val) => toNumber(val).toLocaleString('nb-NO')
    },
    { 
      key: 'market_share_percentage', 
      label: t('common.marketSharePct'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})
    },
    { 
      key: 'leasing_percentage', 
      label: 'Leasing (%)',
      format: (val) => toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})
    },
    { 
      key: 'company_total', 
      label: t('masterModel.companyTotal'),
      format: (val) => toNumber(val).toLocaleString('nb-NO')
    },
    { 
      key: 'avg_owner_age', 
      label: t('masterModel.avgAge'),
      format: (val) => val !== null && val !== undefined ? t('masterModel.years', { value: toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1}) }) : '-'
    },
  ]

  // Prepare export data with computed company_total
  const exportMasterModels = sortedMasterModels.map(mm => ({
    ...mm,
    company_total: getTotalCompanyVehicles(mm)
  }))

  const variantColumns: ColumnDefinition[] = [
    { key: 'model_variant', label: 'Variant' },
    { 
      key: 'total_count', 
      label: t('common.registrations'),
      format: (val) => toNumber(val).toLocaleString('nb-NO')
    },
    { 
      key: 'market_share_percentage', 
      label: t('common.marketSharePct'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})
    },
    { 
      key: 'ev_percentage', 
      label: t('common.evPctExport'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})
    },
    { 
      key: 'leasing_percentage', 
      label: 'Leasing (%)',
      format: (val) => toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})
    },
    { 
      key: 'company_percentage', 
      label: t('common.companyPctExport'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})
    },
  ]

  // Prepare export data with computed company_percentage
  const exportVariants = sortedVariants.map(v => ({
    ...v,
    company_percentage: toNumber(v.company_sales_percentage) + toNumber(v.company_leasing_percentage)
  }))

  if (loading) return <LoadingSpinner />
  if (error) return <ErrorMessage message={error} />

  // Model Variants view (when master model is selected)
  if (selectedMasterModel) {
    return (
      <div className="space-y-6">
        {/* Header with back button */}
        <div className="flex items-center gap-4">
          <button
            onClick={handleBackToList}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-dark-card hover:bg-gray-200 dark:hover:bg-dark-border rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{t('common.backToMasterModels')}</span>
          </button>
          <div>
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
              <span className="hover:text-primary-600 cursor-pointer" onClick={handleBackToList}>{t('common.masterModels')}</span>
              <ChevronRight className="w-3 h-3" />
              <span className="text-gray-900 dark:text-gray-100 font-medium">{selectedMasterModel.brand} {selectedMasterModel.master_model}</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {t('common.variantsTitle', { brand: selectedMasterModel.brand, model: selectedMasterModel.master_model })}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              {t('common.modelVariantsFor', { model: selectedMasterModel.master_model })}
            </p>
          </div>
        </div>

        {/* Master Model Summary */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                <Car className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400">{t('common.totalRegistrations')}</p>
                <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {selectedMasterModel.total_count.toLocaleString('nb-NO')}
                </p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <Zap className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400">{t('common.evShare')}</p>
                <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {selectedMasterModel.ev_percentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
                </p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400">{t('common.marketShare')}</p>
                <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {selectedMasterModel.market_share_percentage.toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %
                </p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <Package className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400">Leasing %</p>
                <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {selectedMasterModel.leasing_percentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
                </p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                <Building2 className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400">{t('common.companyTotal')}</p>
                <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {getTotalCompanyVehicles(selectedMasterModel).toLocaleString('nb-NO')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Variants Table */}
        {variantsLoading ? (
          <LoadingSpinner />
        ) : variantsError ? (
          <div className="card p-8">
            <div className="text-center">
              <Car className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {t('common.variantsComingSoon')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {t('common.variantsEndpointWip')}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-500">
                {t('common.expectedEndpoint')} <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded-sm">GET /api/v1/public/master-models/{selectedMasterModel.master_model_id}/variants</code>
              </p>
            </div>
          </div>
        ) : modelVariants.length > 0 ? (
          <>
            <div className="flex justify-between items-center mb-4">
              <div>
                {variantSortConfig && (
                  <button 
                    onClick={() => setVariantSortConfig(null)}
                    className="btn-secondary text-sm"
                  >
                    {t('common.resetSort')}
                  </button>
                )}
              </div>
              <ExportButtons
                data={exportVariants}
                columns={variantColumns}
                metadata={{
                  title: t('exportTitles.modelVariants', { brand: selectedMasterModel.brand, model: selectedMasterModel.master_model }),
                  dateRange: t('common.dateRange', { start: formatNorwegianDate(startDate), end: formatNorwegianDate(endDate) }),
                  filters: [
                    vehicleFilter === 'personbil' ? t('common.filterPersonbil') : 
                    vehicleFilter === 'varebil' ? t('common.filterVarebil') : '',
                    minePostnummer ? t('common.postnummerFilter', { value: minePostnummer }) : ''
                  ].filter(f => f),
                }}
                disabled={variantsLoading}
              />
            </div>
            <div className="card overflow-x-auto">
              <table className="data-table data-table-compact">
                <thead>
                  <tr>
                    <th className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleVariantSort('model_variant')}>
                      Variant {getVariantSortIcon('model_variant')}
                    </th>
                    <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleVariantSort('total_count')}>
                      {t('common.registrations')} {getVariantSortIcon('total_count')}
                    </th>
                    <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleVariantSort('market_share_percentage')}>
                      {t('common.marketShare')} {getVariantSortIcon('market_share_percentage')}
                    </th>
                    <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleVariantSort('ev_percentage')}>
                      {t('common.evPct')} {getVariantSortIcon('ev_percentage')}
                    </th>
                    <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleVariantSort('leasing_percentage')}>
                      Leasing % {getVariantSortIcon('leasing_percentage')}
                    </th>
                    <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleVariantSort('company_percentage')}>
                      {t('common.companyPct')} {getVariantSortIcon('company_percentage')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedVariants.map((variant, index) => (
                    <tr key={index}>
                      <td className="font-semibold">{variant.model_variant}</td>
                      <td className="text-right">{variant.total_count.toLocaleString('nb-NO')}</td>
                      <td className="text-right text-primary-600 dark:text-primary-400">
                        {variant.market_share_percentage.toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %
                      </td>
                      <td className="text-right text-green-600 dark:text-green-400">
                        {variant.ev_percentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
                      </td>
                      <td className="text-right text-purple-600 dark:text-purple-400">
                        {variant.leasing_percentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
                      </td>
                      <td className="text-right text-orange-600 dark:text-orange-400">
                        {(variant.company_sales_percentage + variant.company_leasing_percentage).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="card p-12 text-center">
            <Car className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400 text-lg">
              {t('common.noVariantsFound')}
            </p>
          </div>
        )}
      </div>
    )
  }

  // Use allBrands for dropdown (populated on initial load)
  const uniqueBrands = allBrands.length > 0 
    ? allBrands.map(b => b.brand) 
    : Array.from(new Set(masterModels.map(mm => mm.brand))).sort()

  // Prepare chart data - show only top 7
  const chartData = {
    labels: sortedMasterModels.slice(0, 7).map(mm => `${mm.brand} ${mm.master_model}`),
    datasets: [
      {
        label: t('common.registrations'),
        data: sortedMasterModels.slice(0, 7).map(mm => mm.total_count),
        backgroundColor: sortedMasterModels.slice(0, 7).map(mm => 
          mm.is_electric === 1 ? 'rgba(34, 197, 94, 0.7)' : 'rgba(14, 165, 233, 0.7)'
        ),
        borderColor: sortedMasterModels.slice(0, 7).map(mm => 
          mm.is_electric === 1 ? 'rgba(34, 197, 94, 1)' : 'rgba(14, 165, 233, 1)'
        ),
        borderWidth: 1,
      },
    ],
  }

  const chartOptions = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    scales: {
      x: {
        ticks: {
          callback: (value: number | string) => Number(value).toLocaleString('nb-NO', { maximumFractionDigits: 0 }),
        },
      },
    },
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {t('masterModel.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {t('masterModel.subtitle')}
          </p>
        </div>
      </div>


      {/* Date Range and Filters */}
      <DateFilterBar
        outputMode={outputMode}
        onOutputModeChange={handleOutputModeChange}
        customStartDate={customStartDate}
        onCustomStartDateChange={setCustomStartDate}
        customEndDate={customEndDate}
        onCustomEndDateChange={setCustomEndDate}
        onApply={handleApplyFilters}
        bottomContent={
          <PostnummerFilter
            enabled={postnummerFilterEnabled}
            onToggle={setPostnummerFilterEnabled}
            compact
          />
        }
      >
        <div className="flex-1 min-w-[150px]">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('masterModel.minRegistrations')}
          </label>
          <input
            type="number"
            value={minCount}
            onChange={(e) => setMinCount(parseInt(e.target.value) || 0)}
            min="0"
            className="input-field"
            placeholder="0"
          />
        </div>
      </DateFilterBar>

      {/* Date Range Alert */}
      {dateRangeAlert && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-800 dark:text-red-200">
            <strong>{t('common.errorPrefix')}</strong> {dateRangeAlert}
          </p>
        </div>
      )}

      {/* Additional Filters */}
      <div className="card p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('masterModel.filterByBrand')}
            </label>
            <select
              value={selectedBrand}
              onChange={(e) => handleBrandChange(e.target.value)}
              className="input-field"
            >
              <option value="">{t('masterModel.allBrands')}</option>
              {uniqueBrands.map(brand => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Period Info Banner */}
      <PeriodInfoBanner
        maxDataDate={maxDataDate}
        outputMode={outputMode}
        periodLabel={t(`outputModes.${outputMode}`)}
        startDate={startDate.toISOString().split('T')[0]}
        endDate={endDate.toISOString().split('T')[0]}
      />

      {/* Market Summary Card */}
      {masterModelSummary && (
        <div className="card p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            {t('masterModel.marketOverviewAll')}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-50 dark:bg-dark-border rounded-lg">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('common.totalRegistrations')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {masterModelSummary.total_count.toLocaleString('nb-NO')}
              </p>
            </div>
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('common.evShare')}</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {masterModelSummary.ev_percentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {t('common.cars', { n: masterModelSummary.ev_count.toLocaleString('nb-NO') })}
              </p>
            </div>
            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('common.leasingShare')}</p>
              <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {masterModelSummary.leasing_percentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {t('common.cars', { n: masterModelSummary.leasing_count.toLocaleString('nb-NO') })}
              </p>
            </div>
            <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('common.marketShare')}</p>
              <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {masterModelSummary.market_share_percentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
              <Car className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400">{t('masterModel.totalModels')}</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {filteredMasterModels.length}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400">{t('common.totalVehicles')}</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {masterModelSummary ? masterModelSummary.total_count.toLocaleString('nb-NO') : filteredMasterModels.reduce((sum, mm) => sum + mm.total_count, 0).toLocaleString('nb-NO')}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
              <Zap className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400">{t('common.ev100Models')}</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {filteredMasterModels.filter(mm => mm.is_electric === 1).length}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
              <Package className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400">{t('masterModel.avgLeasing')}</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {filteredMasterModels.length > 0
                  ? (
                      filteredMasterModels.reduce((sum, mm) => sum + mm.leasing_percentage, 0) /
                      filteredMasterModels.length
                    ).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})
                  : 0}
                %
              </p>
            </div>
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <Building2 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400">{t('common.avgCompanyPct')}</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {filteredMasterModels.length > 0
                  ? (
                      filteredMasterModels.reduce((sum, mm) => 
                        sum + mm.company_sales_percentage + mm.company_leasing_percentage, 0
                      ) / filteredMasterModels.length
                    ).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})
                  : 0}
                %
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="card p-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          {t('masterModel.top7')}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          <span className="inline-block w-3 h-3 bg-green-500 rounded-sm mr-2"></span>
          {t('masterModel.electricModels')}
          <span className="inline-block w-3 h-3 bg-blue-500 rounded-sm ml-4 mr-2"></span>
          {t('masterModel.mixedFuel')}
        </p>
        {filteredMasterModels.length > 0 && (
          <div className="h-[500px]">
            <Bar data={chartData} options={chartOptions} />
          </div>
        )}
      </div>

      {/* Search and Table */}
      <div className="card p-6">
        <div className="mb-4">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={t('common.searchModelOrBrand')}
          />
        </div>

        <div className="flex justify-between items-center mb-4">
          <div>
            {masterModelSortConfig && (
              <button 
                onClick={() => setMasterModelSortConfig(null)}
                className="btn-secondary text-sm"
              >
                {t('common.resetSort')}
              </button>
            )}
          </div>
          <ExportButtons
            data={exportMasterModels}
            columns={masterModelColumns}
            metadata={{
              title: t('exportTitles.masterModelAnalysis'),
              dateRange: t('common.dateRange', { start: formatNorwegianDate(startDate), end: formatNorwegianDate(endDate) }),
              filters: [
                vehicleFilter === 'personbil' ? t('common.filterPersonbil') : 
                vehicleFilter === 'varebil' ? t('common.filterVarebil') : '',
                selectedBrand ? t('masterModel.brandFilter', { brand: selectedBrand }) : '',
                minCount > 0 ? t('masterModel.minRegistrationsFilter', { n: minCount }) : '',
                minePostnummer ? t('common.postnummerFilter', { value: minePostnummer }) : ''
              ].filter(f => f),
            }}
            disabled={loading}
          />
        </div>

        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          {t('masterModel.clickMasterModel')}
        </p>

        <div className="overflow-x-auto">
          <table className="data-table data-table-compact">
            <thead>
              <tr>
                <th className="text-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleMasterModelSort('ranking')} title={t('common.originalRanking')}>
                  # {getMasterModelSortIcon('ranking')}
                </th>
                <th className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleMasterModelSort('master_model')}>
                  {t('common.masterModel')} {getMasterModelSortIcon('master_model')}
                </th>
                <th className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleMasterModelSort('brand')}>
                  {t('common.brand')} {getMasterModelSortIcon('brand')}
                </th>
                <th className="text-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleMasterModelSort('body_type')}>
                  Type {getMasterModelSortIcon('body_type')}
                </th>
                <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleMasterModelSort('total_count')}>
                  {t('common.registrations')} {getMasterModelSortIcon('total_count')}
                </th>
                <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleMasterModelSort('market_share_percentage')}>
                  {t('common.marketShare')} {getMasterModelSortIcon('market_share_percentage')}
                </th>
                <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleMasterModelSort('leasing_percentage')}>
                  Leasing % {getMasterModelSortIcon('leasing_percentage')}
                </th>
                <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleMasterModelSort('company_total')}>
                  {t('masterModel.companyTotal')} {getMasterModelSortIcon('company_total')}
                </th>
                <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleMasterModelSort('avg_owner_age')}>
                  {t('masterModel.avgAge')} {getMasterModelSortIcon('avg_owner_age')}
                </th>
                <th className="text-center">{t('common.action')}</th>
              </tr>
            </thead>
            <tbody>
              {/* Summary Row (TOTAL) */}
              {masterModelSummary && (
                <tr className="bg-primary-50 dark:bg-primary-900/20 border-y-2 border-primary-200 dark:border-primary-800 sticky top-0 z-10">
                  <td className="text-center py-4">
                    <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">—</span>
                  </td>
                  <td className="font-bold text-base uppercase tracking-wide text-primary-700 dark:text-primary-300 py-4">
                    TOTAL
                  </td>
                  <td className="py-4">
                    <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">—</span>
                  </td>
                  <td className="text-center py-4">
                    <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">—</span>
                  </td>
                  <td className="text-right font-bold text-base py-4">{masterModelSummary.total_count.toLocaleString('nb-NO')}</td>
                  <td className="text-right py-4">
                    <span className="font-bold text-base text-blue-600 dark:text-blue-400">
                      {masterModelSummary.market_share_percentage.toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %
                    </span>
                  </td>
                  <td className="text-right py-4">
                    <span className="font-bold text-base text-purple-600 dark:text-purple-400">
                      {masterModelSummary.leasing_percentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
                    </span>
                  </td>
                  <td className="text-right py-4">
                    <span className="font-bold text-base text-orange-600 dark:text-orange-400">
                      {(masterModelSummary.company_sales_count + masterModelSummary.company_leasing_count).toLocaleString('nb-NO')}
                    </span>
                    <span className="text-xs text-gray-500 ml-1">
                      ({(masterModelSummary.company_sales_percentage + masterModelSummary.company_leasing_percentage).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %)
                    </span>
                  </td>
                  <td className="text-right py-4">
                    <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">—</span>
                  </td>
                  <td className="text-center py-4">
                    <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">—</span>
                  </td>
                </tr>
              )}
              
              {/* Master Model Rows */}
              {sortedMasterModels.map((mm) => (
                <tr 
                  key={mm.master_model_id} 
                  className="hover:bg-gray-50 dark:hover:bg-dark-border cursor-pointer transition-colors"
                  onClick={() => handleMasterModelClick(mm)}
                >
                  <td className="text-center text-gray-700 dark:text-gray-300">
                    {mm.ranking ?? '—'}
                  </td>
                  <td className="font-semibold">
                    <div className="flex items-center gap-2">
                      {mm.is_electric === 1 && (
                        <span title={t('common.electric100')}>
                          <Zap className="w-4 h-4 text-green-500" />
                        </span>
                      )}
                      {mm.master_model}
                    </div>
                  </td>
                  <td>
                    <span className="text-xs px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 rounded-sm">
                      {mm.brand}
                    </span>
                  </td>
                  <td className="text-center">
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                      {mm.body_type || '-'}
                    </span>
                  </td>
                  <td className="text-right font-medium">{mm.total_count.toLocaleString('nb-NO')}</td>
                  <td className="text-right">
                    <span className="text-blue-600 dark:text-blue-400">
                      {mm.market_share_percentage.toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %
                    </span>
                  </td>
                  <td className="text-right">
                    <span className="text-purple-600 dark:text-purple-400">
                      {mm.leasing_percentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
                    </span>
                  </td>
                  <td className="text-right">
                    <span className="text-orange-600 dark:text-orange-400">
                      {getTotalCompanyVehicles(mm).toLocaleString('nb-NO')}
                    </span>
                    <span className="text-xs text-gray-500 ml-1">
                      ({((mm.company_sales_percentage + mm.company_leasing_percentage)).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %)
                    </span>
                  </td>
                  <td className="text-right">
                    {mm.avg_owner_age ? t('masterModel.years', { value: mm.avg_owner_age.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1}) }) : '-'}
                  </td>
                  <td className="text-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleMasterModelClick(mm)
                      }}
                      className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      <span className="text-sm font-medium">{t('common.seeVariants')}</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredMasterModels.length === 0 && (
          <div className="p-12 text-center">
            <Car className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400 text-lg">
              {t('common.noModelsFound')}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
              {t('masterModel.noModelsHint')}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
